import { useState, useEffect, useCallback } from 'react';
import { useToast } from '../context/ToastContext';
import {
  getStripeConnectStatus, startStripeConnect, disconnectStripe, setStripeApplicationFee,
  getStripeBalance, createStripePayout, getStripeDashboardLink,
  type StripeConnectStatus, type StripeBalance,
} from '../api';

// Stripe Connect Standard — saetter kundens egen Stripe-konto op saa direct booking-betalinger
// (Bolge 3.4b) lander hos kunden i stedet for RFO. Vises pa Settings-siden.
export default function StripeConnectSection() {
  const { showError, showSuccess } = useToast();
  const [status, setStatus] = useState<StripeConnectStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [feeInput, setFeeInput] = useState('');
  const [balance, setBalance] = useState<StripeBalance | null>(null);

  const reload = useCallback(async () => {
    try {
      const s = await getStripeConnectStatus();
      setStatus(s);
      setFeeInput(String(s.application_fee_percent));
      if (s.status === 'active' && s.account_id) {
        try { setBalance(await getStripeBalance()); }
        catch { /* saldo kan fejle hvis Stripe-konto er ny — ignorer */ }
      } else {
        setBalance(null);
      }
    } catch (e) {
      showError(e instanceof Error ? e.message : 'Kunne ikke hente status');
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => { reload(); }, [reload]);

  // Stripe-onboarding sker i en ny fane. Naar brugeren kommer tilbage (fokus pa dette window)
  // fetcher vi status igen — saa "Forbundet" vises automatisk uden manuel refresh.
  useEffect(() => {
    const onFocus = () => reload();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [reload]);

  // Vis success/error-besked hvis vi lige er kommet tilbage fra Stripe OAuth-callback
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const result = params.get('stripe_connect');
    if (!result) return;
    if (result === 'success') showSuccess('Stripe-konto forbundet');
    else if (result === 'pending') showSuccess('Onboarding modtaget — afventer Stripes verifikation');
    else if (result === 'refresh') showError('Onboarding-link udløb — start forfra');
    else if (result === 'error') showError('Stripe-fejl ved forbindelse');
    else if (result === 'invalid') showError('Ugyldigt callback');
    // Ryd query-string saa beskeden ikke vises ved hver refresh
    window.history.replaceState({}, '', window.location.pathname);
  }, [showError, showSuccess]);

  const handleConnect = async () => {
    setBusy(true);
    try {
      const { url } = await startStripeConnect();
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      showError(e instanceof Error ? e.message : 'Kunne ikke starte forbindelse');
    } finally {
      setBusy(false);
    }
  };

  const handleDisconnect = async () => {
    if (!confirm('Afbryd Stripe-forbindelsen? Direct bookings vil ikke kunne modtage betaling indtil du forbinder igen.')) return;
    setBusy(true);
    try {
      await disconnectStripe();
      showSuccess('Forbindelse afbrudt');
      await reload();
    } catch (e) {
      showError(e instanceof Error ? e.message : 'Fejl');
    } finally {
      setBusy(false);
    }
  };

  const handlePayout = async () => {
    if (!balance || balance.available.amount <= 0) {
      showError('Ingen tilgængelig saldo at udbetale');
      return;
    }
    if (!confirm(`Udbetal ${balance.available.amount.toFixed(2)} DKK til din bankkonto?`)) return;
    setBusy(true);
    try {
      const result = await createStripePayout();
      showSuccess(`Udbetaling startet: ${result.amount.toFixed(2)} DKK`);
      await reload();
    } catch (e) {
      showError(e instanceof Error ? e.message : 'Udbetaling fejlede');
    } finally {
      setBusy(false);
    }
  };

  const handleOpenDashboard = async () => {
    setBusy(true);
    try {
      const { url } = await getStripeDashboardLink();
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      showError(e instanceof Error ? e.message : 'Kunne ikke åbne dashboard');
    } finally {
      setBusy(false);
    }
  };

  const handleFeeSave = async () => {
    const pct = parseFloat(feeInput.replace(',', '.'));
    if (!Number.isFinite(pct) || pct < 0 || pct > 100) {
      showError('Procent skal være mellem 0 og 100');
      return;
    }
    setBusy(true);
    try {
      await setStripeApplicationFee(pct);
      showSuccess('Application fee gemt');
      await reload();
    } catch (e) {
      showError(e instanceof Error ? e.message : 'Fejl');
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <p className="text-sm text-gray-400">...</p>;
  if (!status) return null;

  const isConnected = status.status === 'active' && !!status.account_id;
  const isPending = status.status === 'pending' && !!status.account_id;

  return (
    <section className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="card-heading">Stripe Connect</h3>
          <p className="text-sm text-gray-500 mt-1">
            Direkte betalinger fra dit booking-site lander på din egen Stripe-konto.
          </p>
        </div>
        <div className="flex-shrink-0">
          {isConnected ? (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs bg-sage-50 text-sage-700 border border-sage-200">
              <span className="w-1.5 h-1.5 rounded-full bg-sage-500" />
              Forbundet
            </span>
          ) : isPending ? (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs bg-amber-50 text-amber-700 border border-amber-200">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
              Onboarding ikke færdig
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs bg-gray-50 text-gray-500 border border-gray-200">
              <span className="w-1.5 h-1.5 rounded-full bg-gray-300" />
              Ikke forbundet
            </span>
          )}
        </div>
      </div>

      {!status.configured && (
        <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded p-3">
          Platform-administratoren har endnu ikke konfigureret Stripe Connect. Kontakt RFO-support.
        </div>
      )}

      {/* Guide vises kun før forbindelse — falder bort når man er forbundet */}
      {!isConnected && status.configured && (
        <div className="text-sm bg-cream-50 border border-ink-100 rounded-lg p-4 space-y-3">
          <div className="font-medium text-ink-900">Hvad sker der nu?</div>
          <ol className="list-decimal list-inside space-y-1.5 text-ink-500">
            <li>Du klikker "Forbind Stripe-konto" — en ny fane åbner med Stripe's onboarding.</li>
            <li>Du opretter (eller logger ind på) en Stripe-konto i dit firmas navn.</li>
            <li>Stripe beder om CVR, kontaktoplysninger, et bankkonto-nummer og en identitetsverifikation
              (kørekort eller pas). De er en regulær finansiel institution og er forpligtet til at vide hvem de udbetaler til.</li>
            <li>Når Stripe har godkendt — typisk indenfor minutter — kommer du tilbage hertil og kan modtage betalinger direkte.</li>
          </ol>
          <div className="pt-2 border-t border-ink-100 text-ink-500">
            <span className="font-medium text-ink-900">Sådan virker pengestrømmen:</span> Lejere betaler direkte til
            din Stripe-konto via dit booking-site. Stripe tager ~1,4% + 0,25 kr pr betaling.
            RFO kan tilføje en lille platform-fee oveni (du sætter selv procenten nedenfor — 0 = vi tager intet).
          </div>
          <div className="text-xs text-ink-400">
            <span className="font-medium">Test-mode tip:</span> Du tester på en sandbox-konto.
            I onboarding-flowet kan du klikke "Brug testdata" øverst i venstre side — så udfylder Stripe alt med fake-data så du kan komme igennem uden CVR/dokumenter.
          </div>
        </div>
      )}

      {isConnected && (
        <>
          <div className="text-sm">
            <span className="text-gray-500">Konto-id: </span>
            <span className="font-mono">{status.account_id}</span>
          </div>

          {/* Saldo + udbetaling */}
          {balance && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="bg-cream-50 border border-ink-100 rounded-lg p-4">
                <div className="text-xs uppercase tracking-wide text-gray-500">Tilgængeligt</div>
                <div className="text-2xl font-semibold mt-1 text-ink-900">
                  {balance.available.amount.toLocaleString('da-DK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  <span className="text-sm text-gray-500 ml-1.5">{balance.available.currency.toUpperCase()}</span>
                </div>
                <button type="button" onClick={handlePayout}
                  disabled={busy || balance.available.amount <= 0}
                  className="mt-3 w-full btn btn-primary disabled:opacity-50">
                  {busy ? '...' : 'Udbetal nu'}
                </button>
              </div>
              <div className="bg-cream-50 border border-ink-100 rounded-lg p-4">
                <div className="text-xs uppercase tracking-wide text-gray-500">I transit</div>
                <div className="text-2xl font-semibold mt-1 text-ink-900">
                  {balance.pending.amount.toLocaleString('da-DK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  <span className="text-sm text-gray-500 ml-1.5">{balance.pending.currency.toUpperCase()}</span>
                </div>
                <p className="text-xs text-gray-400 mt-2">Pengene er modtaget men endnu ikke frigivet til udbetaling — Stripe holder dem typisk 2-7 dage.</p>
              </div>
            </div>
          )}

          <button type="button" onClick={handleOpenDashboard} disabled={busy}
            className="text-sm text-sage-700 hover:underline">
            Åbn Stripe Dashboard →
          </button>
        </>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="label">Application fee (%)</label>
          <div className="flex gap-2">
            <input type="number" step="0.1" min="0" max="100"
              className="input flex-1"
              value={feeInput}
              onChange={e => setFeeInput(e.target.value)} />
            <button type="button" onClick={handleFeeSave} disabled={busy}
              className="btn btn-secondary">Gem</button>
          </div>
          <p className="text-xs text-gray-400 mt-1">
            Hvor stor en andel af hver direct booking-betaling RFO tager. 0 = RFO tager intet.
          </p>
        </div>
      </div>

      <div className="flex gap-2 pt-2">
        {isConnected ? (
          <button type="button" onClick={handleDisconnect} disabled={busy}
            className="btn btn-secondary">
            Afbryd forbindelse
          </button>
        ) : isPending ? (
          <>
            <button type="button" onClick={handleConnect} disabled={busy}
              className="btn btn-primary">
              {busy ? '...' : 'Fortsæt onboarding'}
            </button>
            <button type="button" onClick={handleDisconnect} disabled={busy}
              className="btn btn-secondary">
              Start forfra
            </button>
          </>
        ) : (
          <button type="button" onClick={handleConnect}
            disabled={busy || !status.configured}
            className="btn btn-primary">
            {busy ? '...' : 'Forbind Stripe-konto'}
          </button>
        )}
      </div>
    </section>
  );
}
